/**
 * Static Product Reviews
 * Handles JSON data mode: fetch, sort, paginate, render
 */
(function () {
  'use strict';

  const PER_PAGE = 5;

  class StaticReviews {
    constructor(el) {
      this.el = el;
      this.jsonUrl = el.dataset.jsonUrl;
      this.perPage = parseInt(el.dataset.perPage || PER_PAGE, 10);
      this.isMappedProduct = el.dataset.mappedProduct === 'true';
      this.initialized = false;

      this.allReviews = [];
      this.filtered = [];
      this.currentPage = 1;
      this.currentSort = 'pictures-first';

      this.listEl = el.querySelector('.static-reviews__items');
      this.paginateEl = el.querySelector('.static-reviews__paginate');
      this.sortSelect = el.querySelector('.static-reviews__sort-select');
      this.summaryStarsEl = el.querySelector('.static-reviews__summary-stars');
      this.histogramEl = el.querySelector('.static-reviews__histogram');
    }

    async _init() {
      if (this.initialized) return;
      this.initialized = true;

      try {
        const res = await fetch(this.jsonUrl);
        if (!res.ok) throw new Error('Failed to load reviews JSON');
        const payload = await res.json();
        this.allReviews = this._normalizeReviews(payload);
      } catch (e) {
        console.warn('[StaticReviews] Could not load JSON:', e);
        this._renderLoadError();
        return;
      }

      this._computeSummary();
      this._renderSummary();
      if (this.sortSelect) {
        this.sortSelect.value = this.currentSort;
      }
      this._applySort();
      this._renderPage();
      this._renderPagination();

      if (this.sortSelect) {
        this.sortSelect.addEventListener('change', () => {
          this.currentSort = this.sortSelect.value;
          this.currentPage = 1;
          this._applySort();
          this._renderPage();
          this._renderPagination();
        });
      }
    }

    _normalizeReviews(payload) {
      const raw = Array.isArray(payload)
        ? payload
        : (payload && Array.isArray(payload.reviews) ? payload.reviews : []);

      return raw.map((r) => {
        const pictures = Array.isArray(r.picture_urls)
          ? r.picture_urls.join(', ')
          : (r.picture_urls || r.pictures || r.images || '');

        return {
          rating: r.rating || r.stars || r.score || 0,
          review_date: r.review_date || r.created_at || r.date || '',
          reviewer_name: r.reviewer_name || r.author || r.name || '',
          title: r.title || r.review_title || '',
          body: r.body || r.review_body || r.content || '',
          picture_urls: pictures,
          location: r.location || r.reviewer_location || ''
        };
      });
    }

    _computeSummary() {
      const counts = [0, 0, 0, 0, 0]; // index 0 = 1 star ... index 4 = 5 star
      let total = 0;
      let sum = 0;
      this.allReviews.forEach(r => {
        const s = Math.min(5, Math.max(1, parseInt(r.rating, 10)));
        counts[s - 1]++;
        sum += s;
        total++;
      });
      this.summary = { counts, total, avg: total ? (sum / total).toFixed(2) : '0.00' };
    }

    _renderSummary() {
      if (!this.summaryStarsEl || !this.histogramEl) return;
      const { counts, total, avg } = this.summary;
      const avgFloat = parseFloat(avg);

      // Summary stars + average
      this.summaryStarsEl.setAttribute('aria-label', `Average rating is ${avg} stars`);
      this.summaryStarsEl.innerHTML =
        this._starsHtml(avgFloat, true) +
        `<span class="static-reviews__summary-average">${avg} out of 5</span>`;

      // Histogram rows
      this.histogramEl.innerHTML = '';
      const maxCount = Math.max(1, ...counts);
      for (let star = 5; star >= 1; star--) {
        const count = counts[star - 1];
        const rawPct = (count / maxCount) * 100;
        const pct = count > 0 ? Math.max(2, Math.round(rawPct)) : 0;
        const row = document.createElement('div');
        row.className = 'static-reviews__histogram-row';
        row.innerHTML = `
          <div class="static-reviews__histogram-stars" aria-label="${pct}% (${count}) reviews with ${star} star rating">
            ${this._starsHtml(star)}
          </div>
          <div class="static-reviews__histogram-bar">
            <div class="static-reviews__histogram-bar-fill" style="width:${pct}%"></div>
          </div>
          <span class="static-reviews__histogram-count">${count}</span>`;
        this.histogramEl.appendChild(row);
      }
    }

    _applySort() {
      let list = [...this.allReviews];
      switch (this.currentSort) {
        case 'highest-rating':
          list.sort((a, b) => b.rating - a.rating || this._dateMs(b) - this._dateMs(a));
          break;
        case 'lowest-rating':
          list.sort((a, b) => a.rating - b.rating || this._dateMs(b) - this._dateMs(a));
          break;
        case 'with-pictures':
          list = list.filter(r => this._hasPictures(r));
          break;
        case 'pictures-first':
          list.sort((a, b) => {
            const aHas = this._hasPictures(a) ? 1 : 0;
            const bHas = this._hasPictures(b) ? 1 : 0;
            return bHas - aHas || this._dateMs(b) - this._dateMs(a);
          });
          break;
        case 'most-recent':
        default:
          list.sort((a, b) => this._dateMs(b) - this._dateMs(a));
          break;
      }
      this.filtered = list;
    }

    _dateMs(r) {
      return new Date(r.review_date).getTime();
    }

    _hasPictures(r) {
      return typeof r.picture_urls === 'string' && r.picture_urls.trim() !== '';
    }

    _renderPage() {
      if (!this.listEl) return;
      const start = (this.currentPage - 1) * this.perPage;
      const pageItems = this.filtered.slice(start, start + this.perPage);

      this.listEl.innerHTML = pageItems.length
        ? pageItems.map(r => this._reviewHtml(r)).join('')
        : '<p class="static-reviews__empty">No reviews found.</p>';
    }

    _reviewHtml(r) {
      const rating = Math.min(5, Math.max(1, parseInt(r.rating, 10)));
      const title = r.title ? r.title.trim() : '';
      const body = this._esc(r.body || '').replace(/\n/g, '<br>');
      const name = r.reviewer_name || 'Anonymous';
      const date = this._formatDate(r.review_date);
      const location = r.location ? r.location.trim() : '';

      // First image only
      const pics = r.picture_urls
        ? r.picture_urls.split(',').map(u => u.trim()).filter(Boolean)
        : [];
      const hasImage = pics.length > 0;

      const imgHtml = hasImage
        ? `<div class="static-reviews__rev-pics">
            <a class="static-reviews__rev-pic-link" href="${pics[0]}" target="_blank" rel="nofollow" aria-label="Customer review photo by ${this._esc(name)}">
              <img class="static-reviews__rev-pic-img" src="${pics[0]}" alt="Customer review photo" loading="lazy" width="160" height="160">
            </a>
          </div>`
        : '';

      const locationHtml = location
        ? `<span class="static-reviews__rev-location">${this._esc(location)}</span>`
        : '';

      return `
        <div class="static-reviews__review${hasImage ? ' has-image' : ''}">
          <div class="static-reviews__rev-header">
            <div class="static-reviews__rev-profile">
              <span class="static-reviews__rev-author">${this._esc(name)}</span>
              <span class="static-reviews__rev-badge">Verified</span>
            </div>
            <div class="static-reviews__rev-rating-row">
              <div class="static-reviews__rev-stars" aria-label="${rating} star review" role="img">
                ${this._starsHtml(rating)}
              </div>
              <span class="static-reviews__rev-timestamp">${date}</span>
            </div>
          </div>
          <div class="static-reviews__rev-content">
            ${title ? `<b class="static-reviews__rev-title">${this._esc(title)}</b>` : ''}
            <div class="static-reviews__rev-body"><p>${body}</p></div>
            ${imgHtml}
            ${locationHtml}
          </div>
        </div>`;
    }

    activate() {
      if (!this.isMappedProduct) return false;
      this.el.classList.remove('static-reviews--ab-hidden');
      this._toggleJudgeMe(true);
      this._init();
      return true;
    }

    deactivate() {
      this.el.classList.add('static-reviews--ab-hidden');
      this._toggleJudgeMe(false);
    }

    _toggleJudgeMe(hide) {
      const display = hide ? 'none' : '';
      document.querySelectorAll('#judgeme_product_reviews, .product-reviews').forEach((node) => {
        node.style.display = display;
      });
    }

    _renderLoadError() {
      if (this.listEl) {
        this.listEl.innerHTML = '<p class="static-reviews__empty">Reviews are currently unavailable.</p>';
      }
      if (this.paginateEl) {
        this.paginateEl.innerHTML = '';
      }
    }

    _renderPagination() {
      if (!this.paginateEl) return;
      const totalPages = Math.ceil(this.filtered.length / this.perPage);
      if (totalPages <= 1) {
        this.paginateEl.innerHTML = '';
        return;
      }

      const cur = this.currentPage;
      let html = '';
      const range = this._pageRange(cur, totalPages);

      // First + Prev
      if (cur > 1) {
        html += `<button class="static-reviews__page-btn static-reviews__page-btn--nav" data-page="1" aria-label="First page">«</button>`;
        html += `<button class="static-reviews__page-btn static-reviews__page-btn--nav" data-page="${cur - 1}" aria-label="Previous page">‹</button>`;
      }

      // Page numbers — 3-number window
      range.forEach((p) => {
        const isCur = p === cur;
        html += `<button class="static-reviews__page-btn${isCur ? ' static-reviews__page-btn--current' : ''}" data-page="${p}" aria-label="Page ${p}"${isCur ? ' aria-current="page"' : ''}>${p}</button>`;
      });

      // Next + Last
      if (cur < totalPages) {
        html += `<button class="static-reviews__page-btn static-reviews__page-btn--nav" data-page="${cur + 1}" aria-label="Next page">›</button>`;
        html += `<button class="static-reviews__page-btn static-reviews__page-btn--nav" data-page="${totalPages}" aria-label="Last page">»</button>`;
      }

      this.paginateEl.innerHTML = html;

      this.paginateEl.querySelectorAll('[data-page]').forEach(btn => {
        btn.addEventListener('click', () => {
          this.currentPage = parseInt(btn.dataset.page, 10);
          this._renderPage();
          this._renderPagination();
          this.el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    }

    _pageRange(cur, total) {
      const start = Math.max(1, Math.min(cur - 1, total - 2));
      const end = Math.min(total, start + 2);
      const pages = [];
      for (let p = start; p <= end; p++) {
        pages.push(p);
      }
      return pages;
    }

    _starsHtml(rating, roundToWhole) {
      const score = roundToWhole ? Math.round(rating) : rating;
      let html = '';
      for (let i = 1; i <= 5; i++) {
        html += i <= score
          ? '<span class="static-reviews__star" aria-hidden="true">★</span>'
          : '<span class="static-reviews__star static-reviews__star--off" aria-hidden="true">★</span>';
      }
      return html;
    }

    _formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      if (isNaN(d)) return dateStr;
      const now = new Date();
      const diffMs = now - d;
      const diffDays = Math.floor(diffMs / 86400000);
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 14) return '1 week ago';
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 60) return '1 month ago';
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    }

    _esc(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  const FLAG_KEY = '__duskStaticReviewsVariantActive';
  const instances = [];

  document.querySelectorAll('.static-reviews[data-json-url]').forEach((el) => {
    instances.push(new StaticReviews(el));
  });

  function activateStaticReviews() {
    window[FLAG_KEY] = true;
    let hasActivated = false;
    instances.forEach((instance) => {
      hasActivated = instance.activate() || hasActivated;
    });
    return hasActivated;
  }

  function deactivateStaticReviews() {
    window[FLAG_KEY] = false;
    instances.forEach((instance) => {
      instance.deactivate();
    });
  }

  window.DuskStaticReviews = Object.assign(window.DuskStaticReviews || {}, {
    activate: activateStaticReviews,
    deactivate: deactivateStaticReviews
  });

  if (window[FLAG_KEY] === true) {
    activateStaticReviews();
  } else {
    deactivateStaticReviews();
  }
})();
